'use client';
import AddMonitorModal from '@/components/AddMonitorModal';
import Icon from '@/components/common/Icon';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useState } from 'react';
import styles from './AccountContentHeader.module.scss';

/**
 * AccountContentHeader Component
 * Top header for account content area
 *
 * Features:
 * - Mobile Logo (top left - mobile only)
 * - Primary CTA: "Buy Now" (filled blue button)
 * - Secondary CTA: "Add Monitor" (outline button - opens modal)
 * - Auto-hide on scroll (mobile only)
 * - Responsive: Adapts for mobile screens
 * - Compact sizing for minimal space usage
 *
 * @param {boolean} isMobile - Mobile device detection
 * @param {boolean} headerVisible - Header visibility state (for scroll hide on mobile)
 * @param {boolean} hideActions - Hide action buttons (Add Monitor + Buy Now)
 */
const AccountContentHeader = ({ isMobile, headerVisible = true, hideActions = false }) => {
  const [addMonitorModalOpen, setAddMonitorModalOpen] = useState(false);

  const handleAddMonitorClick = (e) => {
    e.preventDefault();
    setAddMonitorModalOpen(true);
  };

  return (
    <>
      <div className={`${styles.headerBar} ${isMobile && !headerVisible ? styles.hidden : ''}`}>
        {/* Mobile Logo - Left Side (Mobile Only) */}
        {isMobile && (
          <Link href="/account/dashboard" className={styles.mobileLogo}>
            <Image
              src="/images/bm-logo-on-white.svg"
              alt="Brandymail"
              width={120}
              height={40}
              priority
            />
          </Link>
        )}

        {/* CTAs - Right Side (hidden when hideActions is true) */}
        {!hideActions && (
          <div className={styles.ctaGroup}>
            {/* Secondary CTA: Add Monitor - Opens Modal */}
            <button onClick={handleAddMonitorClick} className={styles.secondaryCta}>
              <Icon name="Plus" size={14} />
              {!isMobile && <span>Add Monitor</span>}
            </button>

            {/* Primary CTA: Buy Now */}
            <Link
              href="/products/digicert-mark-certificates"
              className={styles.primaryCta}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="ShoppingCart" size={14} />
              {!isMobile && <span>Buy Now</span>}
            </Link>
          </div>
        )}
      </div>

      {/* Add Monitor Modal */}
      <AddMonitorModal
        open={addMonitorModalOpen}
        onCancel={() => setAddMonitorModalOpen(false)}
        mode="manual"
      />
    </>
  );
};

AccountContentHeader.propTypes = {
  isMobile: PropTypes.bool,
  headerVisible: PropTypes.bool,
  hideActions: PropTypes.bool,
};

export default AccountContentHeader;
